
import { supabase } from '@/lib/supabase';
import { DetailedBotAccount } from '@/types/trader';

// Fetch connected accounts for a premium bot
export const fetchPremiumBotAccounts = async (
  botId: string,
  userId: string
): Promise<DetailedBotAccount[]> => {
  try {
    console.log(`Fetching connected accounts for premium bot ${botId} for user ${userId}`);
    
    // Get all the bot accounts for this bot and user using original_bot_id
    const { data: botAccounts, error: botAccountsError } = await supabase
      .from('bot_accounts')
      .select('id, bot_id, user_id, api_connection_id, volume_multiplier, status, accountid_trading, original_bot_id, live, is_reverse_position, added_date, created_at, updated_at')
      .eq('original_bot_id', botId) // Changed from bot_id to original_bot_id
      .eq('user_id', userId);
    
    if (botAccountsError) {
      console.error('Error fetching bot accounts:', botAccountsError);
      throw botAccountsError;
    }
    
    console.log(`Found ${botAccounts?.length || 0} bot accounts`);
    
    if (!botAccounts || botAccounts.length === 0) {
      return [];
    }
    
    // For each bot account, fetch the related data
    const detailedAccounts: DetailedBotAccount[] = [];
    
    for (const account of botAccounts) {
      if (!account.api_connection_id) {
        console.log(`No API connection ID for bot account ${account.id}`);
        continue;
      }
      
      // Get API connection details
      const { data: apiConnection, error: apiError } = await supabase
        .from('api_connections')
        .select('*')
        .eq('id', account.api_connection_id)
        .single();
      
      if (apiError) {
        console.error(`Error fetching API connection for bot account ${account.id}:`, apiError);
        continue;
      }
      
      // Get trading accounts for this API connection
      const { data: tradingAccounts, error: tradingError } = await supabase
        .from('trading_accounts')
        .select('*')
        .eq('api_connection_id', account.api_connection_id);
      
      if (tradingError) {
        console.error(`Error fetching trading accounts for API connection ${account.api_connection_id}:`, tradingError);
        continue;
      }
      
      // Get CSP account details
      const { data: cspAccount, error: cspError } = await supabase
        .from('csp_accounts')
        .select('*')
        .eq('id', apiConnection.csp_account_id)
        .single();
      
      if (cspError) {
        console.error(`Error fetching CSP account for API connection ${account.api_connection_id}:`, cspError);
        continue;
      }
      
      // Build detailed account object
      const detailedAccount: DetailedBotAccount = {
        ...account,
        api_connection: {
          ...apiConnection,
          trading_accounts: tradingAccounts || []
        },
        trading_account: tradingAccounts?.[0],
        csp_account: {
          csp_account_name: cspAccount.csp_account_name,
          broker: cspAccount.broker,
          prop_firm: cspAccount.prop_firm
        }
      };
      
      detailedAccounts.push(detailedAccount);
    }
    
    return detailedAccounts;
  } catch (error) {
    console.error('Error in fetchPremiumBotAccounts:', error);
    throw error;
  }
};

// Toggle the status of a premium bot account (connect/disconnect)
export const togglePremiumBotAccountStatus = async (
  accountId: string,
  isConnected: boolean
): Promise<{ success: boolean, id: string }> => {
  try {
    console.log(`Toggling account ${accountId} connection status to ${isConnected ? 'connected' : 'disconnected'}`);
    
    // Update the status of the bot account
    const { data, error } = await supabase
      .from('bot_accounts')
      .update({
        status: isConnected ? 'connected' : 'disconnected',
        updated_at: new Date().toISOString()
      })
      .eq('id', accountId)
      .select('id')
      .single();
    
    if (error) {
      console.error('Error toggling account status:', error);
      // Enhance error with the account connection ID for reference
      const enhancedError = {
        ...error,
        accountConnectionId: accountId
      };
      throw enhancedError;
    }
    
    console.log('Successfully updated account status:', data);
    
    return {
      success: true,
      id: data.id
    };
  } catch (error) {
    console.error('Error in togglePremiumBotAccountStatus:', error);
    throw error;
  }
};

// Delete a premium bot account connection
export const deletePremiumBotAccount = async (
  accountId: string
): Promise<{ success: boolean }> => {
  try {
    console.log(`Deleting bot account ${accountId}`);
    
    // Delete the bot account
    const { error } = await supabase
      .from('bot_accounts')
      .delete()
      .eq('id', accountId);
    
    if (error) {
      console.error('Error deleting bot account:', error);
      throw error;
    }
    
    console.log('Successfully deleted bot account');
    
    return {
      success: true
    };
  } catch (error) {
    console.error('Error in deletePremiumBotAccount:', error);
    throw error;
  }
};
